import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, FeatureGroup, GeoJSON } from "react-leaflet";
import { cityBoundaries, cityStyle } from "../../../../helpers/constants";
import { Input } from "../../../../components/inputs/Input";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { CFormTextarea, CSpinner } from "@coreui/react";
import { EditControl } from "react-leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";
import { yupResolver } from "@hookform/resolvers/yup";
import apiService from "../../../../service/apiService";
import { useCookie } from "../../../../hooks/useCookie";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../../../../hooks/useToast";
import { general } from "../../../../locales/general";

const newSpecialZoneSchema = yup.object().shape({
  name: yup.string().required("Ce champ est obligatoire"),
  price: yup.number().required("Ce champ est obligatoire"),
  isActive: yup.boolean().default(false),
  description: yup.string().optional().nullable(),
  geometry: yup.array().optional().nullable(),
});

export default function CreateTaxiSpecialZone({
  mode = "create",
}: {
  mode: "create" | "update";
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [geometryInitialValue, setGeometryInitialValue] = useState<
    [number, number][] | null
  >(null);
  const { id: specialZoneId }: { id?: string } = useParams();
  const { token } = useCookie("vToken");
  const navigate = useNavigate();
  const { toastError, toastSuccess } = useToast();
  const {
    handleSubmit,
    control,
    setValue: setFormValue,
  } = useForm({
    resolver: yupResolver(newSpecialZoneSchema),
  });

  const createSpecialZoneMutation = useMutation({
    mutationFn: (data: any) =>
      apiService.MakePostRequest("rides/special-zones", data, token),
    mutationKey: ["special-zones"],
  });

  const updateSpecialZoneMutation = useMutation({
    mutationFn: (data: any) =>
      apiService.MakePutRequest(
        `rides/special-zones/${specialZoneId}`,
        data,
        token,
      ),
    mutationKey: ["special-zones", specialZoneId],
  });

  const {
    data: specialZone,
    // isLoading: isSpecialZoneLoading,
    status: specialZoneStatus,
  } = useQuery<RideSpecialZone>(
    ["special-zones", specialZoneId],
    () => {
      return apiService.MakeGetRequest(
        `rides/special-zones/${specialZoneId}`,
        token,
      );
    },
    {
      enabled: mode === "update",
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      cacheTime: 0,
    },
  );

  useEffect(() => {
    if (specialZoneStatus === "success") {
      setFormValue("name", specialZone?.name);
      setFormValue("price", specialZone?.price);
      console.log("🚀 ~ CreateTaxiSpecialZone ~ price:", specialZone?.price);
      setFormValue("isActive", specialZone?.isActive ?? false);
      setFormValue("description", specialZone?.description);
      setFormValue("geometry", specialZone?.geometry);
      setGeometryInitialValue(specialZone?.geometry);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialZoneStatus, specialZoneId]);

  useEffect(() => {
    if (mode === "update") {
      setIsLoading(true);
      if (!specialZoneId) {
        navigate("/taxi/special-zones");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialZoneId, mode]);

  const onSubmit = (data: any) => {
    if (!Array.isArray(data.geometry) || data?.geometry?.length < 2) {
      toastError("Veuillez sélectionner la zone spéciale sur la carte");
      return;
    }
    if (
      data.geometry[0][0] !== data.geometry[data.geometry.length - 1][0] ||
      data.geometry[0][1] !== data.geometry[data.geometry.length - 1][1]
    ) {
      data.geometry.push(data.geometry[0]);
    }
    if (mode === "update") {
      updateSpecialZoneMutation.mutate(data, {
        onSuccess: () => {
          toastSuccess("Zone spéciale mise à jour avec succès");
          navigate("/taxi/special-zones");
        },
        onError: (error) => {
          toastError(general.fr.message.operationFailed);
        },
      });
    } else {
      createSpecialZoneMutation.mutate(data, {
        onSuccess: () => {
          toastSuccess("Zone spéciale créée avec succès");
          navigate("/taxi/special-zones");
        },
        onError: (error) => {
          toastError(general.fr.message.operationFailed);
        },
      });
    }
  };

  return (
    <div style={{ position: "relative" }} key={specialZoneId}>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgb(255 255 255 / 33%)",
          display: isLoading ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
        }}
      >
        <CSpinner color="danger" />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          style={{
            display: "flex",
            height: "calc(100vh - 48px - 63px)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              width: "50%",
              height: "100vh",
              overflowY: "auto",
              paddingRight: "2rem",
              paddingLeft: "1.5rem",
            }}
          >
            <h1 className="text-lg fw-bold mb-4">Créer une Zone Spéciale</h1>
            <div
              style={{
                backgroundColor: "white",
                padding: "2rem",
                borderRadius: "8px",
              }}
            >
              {/* Form fields */}
              <Input
                name="name"
                control={control}
                type="text"
                label="Nom"
                placeholder="Nom"
                required
              />

              <div className="mt-3">
                <Input
                  name="price"
                  control={control}
                  type="number"
                  label="Prix"
                  required
                />
              </div>

              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <div className="d-flex align-items-center gap-1 mt-3">
                    <input
                      id="isActive"
                      type="checkbox"
                      {...(field as any)}
                      checked={field.value}
                    />
                    <label htmlFor="isActive">Actif</label>
                  </div>
                )}
              />

              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <div className="mt-3">
                    <label htmlFor="description">Description</label>
                    <CFormTextarea
                      id="description"
                      {...(field as any)}
                      placeholder="Description"
                    />
                  </div>
                )}
              />

              <div
                className="d-flex justify-content-end"
                style={{
                  marginTop: "2rem",
                }}
              >
                <button
                  className="btn btn-primary text-white me-2"
                  type="button"
                  onClick={() => navigate("/taxi/special-zones")}
                  disabled={createSpecialZoneMutation.isLoading}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-danger text-white"
                  type="submit"
                  disabled={createSpecialZoneMutation.isLoading}
                >
                  {createSpecialZoneMutation.isLoading
                    ? "Création en cours..."
                    : "Sauvegarder"}
                </button>
              </div>
            </div>
          </div>
          <div
            style={{
              width: "50%",
              height: "100vh",
              backgroundColor: "#dee2e6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
            }}
          >
            <Controller
              name="geometry"
              control={control}
              render={({ field }) => (
                <MapContainer
                  center={[30.427755, -9.598107]}
                  zoom={13}
                  style={{ height: "100%", width: "100%", zIndex: 40 }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
                  />
                  <GeoJSON data={cityBoundaries} style={cityStyle} />

                  <FeatureGroup>
                    <DrawTools
                      setValue={field.onChange}
                      initialValue={geometryInitialValue}
                    />
                  </FeatureGroup>
                </MapContainer>
              )}
            />
          </div>
        </div>
      </form>
    </div>
  );
}

const DrawTools = ({
  initialValue,
  setValue,
}: {
  initialValue: [number, number][] | null;
  setValue: (value: any) => void;
}) => {
  const hasInitialized = useRef(false);

  const _onMounted = (drawControl: any) => {
    if (hasInitialized.current) return;

    console.log("🚀 ~ _onMounted ~ drawControl:", drawControl);

    // Add initial geometry if it exists
    if (initialValue && drawControl) {
      // The correct path to access the feature group
      const drawnItems = drawControl._map._layers;

      // Find the FeatureGroup layer
      let featureGroup: any = null;
      Object.values(drawnItems).forEach((layer: any) => {
        if (layer instanceof window.L.FeatureGroup) {
          featureGroup = layer;
        }
      });

      if (featureGroup) {
        // Create a Leaflet polygon from coordinates
        const leafletPolygon = window.L.polygon(
          initialValue.map((coord: [number, number]) => [coord[1], coord[0]]),
        );

        // Add to the feature group
        featureGroup.addLayer(leafletPolygon);

        // Set the initial value
        setValue(initialValue);

        hasInitialized.current = true;

        const bounds = leafletPolygon.getBounds();
        drawControl._map.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 15,
        });
      }
    }
  };

  const _onEdited = (e: any) => {
    const layers: any[] = [];
    e.layers.eachLayer((layer: any) => {
      const geoJson = layer.toGeoJSON();
      if (geoJson.geometry.type === "Polygon") {
        layers.push(geoJson);
      }
    });
    if (layers.length === 0) return;
    const firstLayer = layers[0];

    const coords = firstLayer.geometry.coordinates[0];
    setValue(coords);
  };

  const _onCreated = (e: any) => {
    if (e.layerType === "polygon") {
      const coords = e.layer.toGeoJSON().geometry.coordinates[0];
      setValue(coords);
    }
  };

  const _onDeleted = (e: any) => {
    setValue(null);
  };

  return (
    <EditControl
      position="topleft"
      onMounted={_onMounted}
      onEdited={_onEdited}
      onCreated={_onCreated}
      onDeleted={_onDeleted}
      draw={{
        polyline: false,
        rectangle: false,
        circle: false,
        polygon: true,
        marker: false,
        circlemarker: false,
      }}
    />
  );
};
